import { dynamicIconImports } from 'lucide-react/dynamic';

import type { IconData } from '../../components/ui/icon-picker';

/**
 * Extra Lucide icon names (kebab-case) for custom social links — link, web, media, brands-agnostic, etc.
 * Only names that exist in both `icons-data` and `dynamicIconImports` are kept.
 */
const EXTRA_SOCIAL_LINK_ICON_NAMES = new Set([
  'link',
  'link-2',
  'unlink',
  'external-link',
  'share',
  'share-2',
  'forward',
  'reply',
  'send',
  'mail',
  'mail-open',
  'inbox',
  'message-circle',
  'message-square',
  'messages-square',
  'phone',
  'phone-call',
  'smartphone',
  'voicemail',
  'rss',
  'radio',
  'podcast',
  'mic',
  'mic-2',
  'video',
  'camera',
  'image',
  'images',
  'clapperboard',
  'film',
  'music',
  'music-2',
  'music-3',
  'music-4',
  'headphones',
  'disc',
  'tv',
  'monitor',
  'monitor-smartphone',
  'cast',
  'airplay',
  'screen-share',
  'presentation',
  'globe',
  'globe-2',
  'earth',
  'languages',
  'bookmark',
  'bookmark-plus',
  'tag',
  'hash',
  'at-sign',
  'qr-code',
  'app-window',
  'layout-grid',
  'network',
  'orbit',
  'heart',
  'heart-handshake',
  'star',
  'thumbs-up',
  'users',
  'user',
  'user-plus',
  'user-round',
  'sparkles',
  'zap',
  'flame',
  'crown',
  'flag',
  'map-pin',
  'navigation',
  'compass',
  'plane',
  'train-front',
  'bike',
  'car',
  'rocket',
  'coffee',
  'gamepad-2',
  'palette',
  'feather',
  'megaphone',
  'bell',
  'bell-ring',
  'calendar',
  'calendar-clock',
  'clock',
  'building',
  'building-2',
  'briefcase',
  'store',
  'shopping-bag',
  'trophy',
  'award',
  'badge',
  'badge-check',
  'code',
  'code-2',
  'terminal',
  'server',
  'cloud',
  'wifi',
  'bluetooth',
  'newspaper',
  'file-text',
  'sticky-note',
  'pen-line',
  'headset',
  'volume-2',
  'webcam',
  'image-play',
  'gallery-horizontal',
  'shield',
  'lock',
  'key',
  'gift',
  'party-popper',
  'cake',
  'utensils',
  'wine',
  'beer',
  'dog',
  'cat',
  'bird',
  'flower-2',
  'tree-palm',
  'mountain',
  'sun',
  'moon',
  'cloud-sun',
  'umbrella',
  'anchor',
  'ship',
  'gem',
  'infinity',
]);

const CATEGORY_ALLOW = new Set(['social', 'communication', 'connectivity']);

function isDynamicIcon(name: string): boolean {
  return name in dynamicIconImports;
}

/**
 * Curated list for the custom-social icon picker: communication-related categories from the registry
 * plus common “link / presence / media” icons.
 */
let cached: IconData[] | null = null;

export async function getSocialCustomIconPickerList(): Promise<IconData[]> {
  if (cached) return cached;

  const { iconsData } = await import('../../components/ui/icons-data');

  cached = iconsData
    .filter((icon: IconData) => {
      if (!isDynamicIcon(icon.name)) return false;
      if (EXTRA_SOCIAL_LINK_ICON_NAMES.has(icon.name)) return true;
      const cats = icon.categories ?? [];
      return cats.some((c) => CATEGORY_ALLOW.has(c));
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return cached;
}
